import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../../../components/base/Button";
import SEO from "../../../components/base/SEO";
import Footer from "../../../components/feature/Footer";
import Header from "../../../components/feature/Header";

type ERPCaseStudy = {
  slug: string;
  href?: string;
  industry: string;
  companyType: string;
  title: string;
  summary: string;
  pains: string[];
  modules: string[];
  outcomes: string[];
  stage: "已整理" | "撰寫中" | "可新增";
};

type PainPoint = {
  icon: string;
  title: string;
  description: string;
  relatedCases: string[];
};

type ComparisonRow = {
  industry: string;
  commonProblem: string;
  departments: string;
  modules: string;
  firstStep: string;
};

const erpCaseStudies: readonly ERPCaseStudy[] = [
  {
    slug: "tour-bus",
    href: "/erp/case-studies/tour-bus",
    industry: "旅遊巴 / 包車公司",
    companyType: "車隊營運、包車報價、月結客戶",
    title: "由報價、派車到月結對數放回同一套 ERP 流程",
    summary:
      "前線接單、後勤排車、財務追數經常各自用 Excel 和 WhatsApp 跟進，最容易出現同一張單有幾個版本。",
    pains: ["報價轉正式訂單要重覆輸入", "派車安排與客戶資料分散", "月結對數要逐張單追 reference"],
    modules: ["銷售管理", "訂單管理", "財務會計", "客戶 CRM"],
    outcomes: ["訂單狀態集中", "收款追蹤更清楚", "高峰期排車資料更易管理"],
    stage: "已整理",
  },
  {
    slug: "retail-chain",
    industry: "零售店 / 連鎖門市",
    companyType: "多門市、多倉、補貨與調撥",
    title: "把門市補貨、倉庫出貨、採購和庫存數字接起來",
    summary:
      "當門市、倉庫和採購分開運作，管理層通常要等人手匯總才知道哪裡缺貨、哪裡積壓。",
    pains: ["門市補貨和採購不同步", "調撥靠訊息通知", "庫存更新慢"],
    modules: ["庫存管理", "採購管理", "銷售管理", "報表分析"],
    outcomes: ["補貨節奏更穩定", "多倉存量可追蹤", "採購決策更有依據"],
    stage: "已整理",
  },
  {
    slug: "trading-wholesale",
    industry: "貿易 / 批發公司",
    companyType: "B2B 銷售、供應商採購、應收應付",
    title: "把客戶報價、供應商成本、出貨和應收管理串起來",
    summary:
      "業務、採購、營運和財務各自處理交易的一段，容易令毛利、出貨和收款風險到月底才看得清楚。",
    pains: ["客戶價格和付款條件分散", "採購成本難即時對照", "應收跟進依賴個人經驗"],
    modules: ["銷售管理", "採購管理", "財務會計", "供應商管理"],
    outcomes: ["交易脈絡完整", "毛利更早看見", "現金流預測更穩"],
    stage: "已整理",
  },
  {
    slug: "food-supplier",
    industry: "食品供應 / 食材批發",
    companyType: "批次、到期日、每日送貨、客戶價目表",
    title: "處理批次庫存、客戶價格、送貨單和月結發票",
    summary:
      "食材供應商通常訂單頻密、品項多、價格表多，ERP 要先穩住每日落單、執貨和對數。",
    pains: ["不同客戶有不同價目表", "批次和到期日難追", "送貨單與發票資料不一致"],
    modules: ["庫存管理", "銷售管理", "財務會計", "報表分析"],
    outcomes: ["批次追蹤更清晰", "發票資料少出錯", "每日出貨節奏更順"],
    stage: "撰寫中",
  },
  {
    slug: "engineering-contractor",
    industry: "工程 / 維修服務公司",
    companyType: "項目報價、物料採購、工單、成本核算",
    title: "由工程報價、採購、工單到項目成本建立管理脈絡",
    summary:
      "工程公司很多資料散落在報價單、採購單、現場記錄和發票，最需要看清每個項目的成本和進度。",
    pains: ["項目成本要人手匯總", "採購與工單未能對上", "完工後才知道利潤偏差"],
    modules: ["項目管理", "採購管理", "庫存管理", "財務會計"],
    outcomes: ["項目成本更早掌握", "採購和工單可追蹤", "管理層更易看進度"],
    stage: "撰寫中",
  },
  {
    slug: "medical-supplies",
    industry: "醫療用品 / 器材公司",
    companyType: "產品批號、客戶訂單、合規資料、應收",
    title: "管理產品批號、客戶訂單、供應商資料和收款狀態",
    summary:
      "醫療用品公司除了銷售和庫存，也要重視批號、文件、供應商和客戶交付紀錄是否能快速追溯。",
    pains: ["批號資料分散", "客戶文件和訂單未集中", "欠款和交付狀態難同時查看"],
    modules: ["庫存管理", "客戶 CRM", "銷售管理", "供應商管理"],
    outcomes: ["追溯資料更完整", "客戶跟進更集中", "應收和交付狀態同時可見"],
    stage: "可新增",
  },
  {
    slug: "beauty-salon-group",
    industry: "美容 / 服務連鎖",
    companyType: "套票、產品庫存、分店銷售、會員紀錄",
    title: "把會員、套票、產品銷售和分店庫存整理成同一視角",
    summary:
      "服務型連鎖常見問題是會員資料、套票消耗、產品庫存和分店收入分開管理，難看清真實營運表現。",
    pains: ["套票和產品銷售分開記", "分店庫存調撥不清楚", "會員跟進依賴前線記憶"],
    modules: ["客戶 CRM", "銷售管理", "庫存管理", "報表分析"],
    outcomes: ["會員紀錄集中", "分店數字更透明", "產品補貨更易預測"],
    stage: "可新增",
  },
  {
    slug: "manufacturing-sme",
    industry: "小型製造 / 加工公司",
    companyType: "BOM、原材料、加工工序、成品庫存",
    title: "由原材料採購、加工進度到成品出貨建立 ERP 基礎",
    summary:
      "製造型中小企未必一開始需要大型 MRP，但通常需要先把原材料、半成品、成品和採購成本整理好。",
    pains: ["原材料存量不準", "加工進度靠人問", "成品成本難即時計算"],
    modules: ["生產管理", "庫存管理", "採購管理", "報表分析"],
    outcomes: ["物料需求更清楚", "加工進度可追蹤", "成本估算更穩定"],
    stage: "可新增",
  },
  {
    slug: "education-provider",
    industry: "教育中心 / 培訓機構",
    companyType: "課程銷售、學生資料、收款、教材庫存",
    title: "把課程報名、學生資料、收款和教材管理接起來",
    summary:
      "教育機構的 ERP 不一定由庫存開始，反而常由客戶資料、課程銷售、收款和營運報表開始。",
    pains: ["學生資料和付款紀錄分散", "課程收入難按班別查看", "教材或產品庫存未集中"],
    modules: ["客戶 CRM", "銷售管理", "財務會計", "報表分析"],
    outcomes: ["學生跟進更清楚", "課程收入可分類", "收款狀態更易核對"],
    stage: "可新增",
  },
  {
    slug: "logistics-3pl",
    industry: "物流 / 第三方倉儲",
    companyType: "倉儲收費、客戶訂單、出入庫、月結",
    title: "把倉儲、配送、客戶收費和月結資料接到 ERP",
    summary:
      "物流公司通常已有 WMS 或 TMS，但 ERP 可以接住客戶合約、收費、發票、應收和管理報表。",
    pains: ["營運系統和財務資料分開", "客戶收費規則複雜", "月結要重新整理出入庫數據"],
    modules: ["財務會計", "客戶 CRM", "報表分析", "系統整合"],
    outcomes: ["營運數據可轉收費", "月結效率提高", "客戶盈利更易分析"],
    stage: "可新增",
  },
] as const;

const painPoints: readonly PainPoint[] = [
  {
    icon: "ri-file-excel-2-line",
    title: "Excel 開始對不到數",
    description: "訂單、庫存、採購、發票和付款各有一份表，月底才發現版本不一致。",
    relatedCases: ["零售店 / 連鎖門市", "貿易 / 批發公司", "食品供應 / 食材批發"],
  },
  {
    icon: "ri-whatsapp-line",
    title: "流程靠 WhatsApp 交接",
    description: "同事知道怎樣做，但資料沒有回到系統，請假或轉人就容易斷線。",
    relatedCases: ["旅遊巴 / 包車公司", "工程 / 維修服務公司", "物流 / 第三方倉儲"],
  },
  {
    icon: "ri-bank-card-line",
    title: "月結、應收和對數太慢",
    description: "服務完成、貨已送、發票已出，但收款和 reference 仍要逐張追。",
    relatedCases: ["旅遊巴 / 包車公司", "貿易 / 批發公司", "物流 / 第三方倉儲"],
  },
  {
    icon: "ri-store-2-line",
    title: "多門市或多倉難同步",
    description: "補貨、調撥、缺貨和積壓很難用同一套數字判斷。",
    relatedCases: ["零售店 / 連鎖門市", "美容 / 服務連鎖", "食品供應 / 食材批發"],
  },
  {
    icon: "ri-price-tag-3-line",
    title: "客戶價格和成本難管理",
    description: "不同客戶、不同產品、不同供應商成本同時變動，毛利要靠人手估。",
    relatedCases: ["貿易 / 批發公司", "醫療用品 / 器材公司", "小型製造 / 加工公司"],
  },
  {
    icon: "ri-dashboard-3-line",
    title: "管理層看不到即時狀態",
    description: "老闆想知道未完成訂單、庫存風險、欠款和項目成本，但資料要等人整理。",
    relatedCases: ["工程 / 維修服務公司", "小型製造 / 加工公司", "教育中心 / 培訓機構"],
  },
] as const;

const comparisonRows: readonly ComparisonRow[] = [
  {
    industry: "旅遊巴 / 包車",
    commonProblem: "報價、派車、服務完成和月結資料分散",
    departments: "業務、車務、財務",
    modules: "銷售、訂單、CRM、財務",
    firstStep: "先整理報價轉訂單與月結對數",
  },
  {
    industry: "零售 / 連鎖",
    commonProblem: "補貨、調撥和庫存數字更新慢",
    departments: "門市、倉庫、採購",
    modules: "庫存、採購、銷售、報表",
    firstStep: "先建立門市補貨和調撥流程",
  },
  {
    industry: "貿易 / 批發",
    commonProblem: "客戶價格、供應商成本和應收未能同時查看",
    departments: "業務、採購、營運、財務",
    modules: "銷售、採購、供應商、財務",
    firstStep: "先把報價到收款流程接起來",
  },
  {
    industry: "工程 / 維修",
    commonProblem: "項目成本、採購和工單紀錄分散",
    departments: "工程、採購、財務",
    modules: "項目、採購、庫存、財務",
    firstStep: "先用項目編號串起採購和成本",
  },
  {
    industry: "食品供應",
    commonProblem: "批次、到期日、價目表和送貨單難核對",
    departments: "銷售、倉庫、配送、財務",
    modules: "庫存、銷售、財務、報表",
    firstStep: "先整理客戶價目表和批次庫存",
  },
] as const;

const aiFaqItems = [
  {
    q: "香港中小企通常什麼時候需要 ERP？",
    a: "當訂單、庫存、採購、收款和客戶資料開始分散在 Excel、WhatsApp、紙本和不同同事手上，ERP 的價值就不是多一個系統，而是把日常流程重新接成同一套可追蹤資料。",
  },
  {
    q: "ERP 案例一定要公開客戶公司名才有價值嗎？",
    a: "不一定。對正在評估 ERP 的公司來說，行業、流程、痛點、模組和改善方式同樣重要。匿名案例如果把業務情境講清楚，反而更容易讓相似公司判斷是否適合。",
  },
  {
    q: "不同公司導入 ERP 應該先做哪個模組？",
    a: "通常不應由功能表開始，而應由最混亂、最多人重覆輸入、最影響現金流或交付的一段開始。例如貿易公司常由報價到收款開始，零售公司常由庫存和補貨開始。",
  },
  {
    q: "AUTOCHAIN 的 ERP 適合哪些公司？",
    a: "較適合已有穩定日常流程，但資料仍靠 Excel、人手搬資料和訊息交接的中小企。我們會先理解現有 SOP，再建議第一階段應落地的 ERP 範圍。",
  },
] as const;

const seoTopics = [
  "香港 ERP 案例",
  "中小企 ERP 導入",
  "ERP 成功案例",
  "零售 ERP",
  "批發 ERP",
  "ERP SOP 流程",
  "採購庫存財務整合",
  "Excel 轉 ERP",
] as const;

export default function ERPCaseStudiesPage() {
  const { t } = useTranslation();
  const isZh = t("nav.language") === "中";
  const pageTitle = "ERP 案例分享 | 香港中小企 ERP 行業案例與導入痛點 | AUTOCHAIN";
  const pageDescription =
    "AUTOCHAIN ERP 案例中心，整理香港中小企在零售、批發、旅遊巴、工程、食品供應、物流等行業的 ERP 導入痛點、常用模組、流程改善和 FAQ。";

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const openWhatsApp = (zhMessage: string, enMessage: string) => {
    const message = encodeURIComponent(isZh ? zhMessage : enMessage);
    window.open(`https://wa.me/85294252903?text=${message}`, "_blank");
  };

  const structuredCases = erpCaseStudies.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    url: `https://autochain.com.hk${item.href ?? `/erp/case-studies#${item.slug}`}`,
    name: `${item.industry} ERP 案例｜${item.title}`,
    item: {
      "@type": "Article",
      headline: `${item.industry} ERP 案例｜${item.title}`,
      abstract: item.summary,
      keywords: [...item.modules, item.industry, item.companyType],
      about: item.pains,
      publisher: {
        "@type": "Organization",
        name: "AUTOCHAIN TECHNOLOGY",
      },
      inLanguage: "zh-Hant-HK",
    },
  }));

  return (
    <div className="min-h-screen bg-[#f7f5ef] text-slate-900">
      <SEO
        title={pageTitle}
        description={pageDescription}
        keywords="ERP 案例分享,香港 ERP 案例,中小企 ERP,ERP 成功案例,ERP 導入痛點,零售 ERP,批發 ERP,ERP SOP 流程,Excel 轉 ERP"
        ogTitle={pageTitle}
        ogDescription={pageDescription}
        ogImage="https://autochain.com.hk/erp-og-image.png"
        canonicalUrl="/erp/case-studies"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "ERP 案例分享",
            description: pageDescription,
            url: "https://autochain.com.hk/erp/case-studies",
            inLanguage: "zh-Hant-HK",
            isPartOf: {
              "@type": "WebSite",
              name: "AUTOCHAIN",
              url: "https://autochain.com.hk/",
            },
            about: seoTopics,
            publisher: {
              "@type": "Organization",
              name: "AUTOCHAIN TECHNOLOGY",
              url: "https://autochain.com.hk/",
              logo: {
                "@type": "ImageObject",
                url: "https://autochain.com.hk/logo.png",
              },
            },
            mainEntity: {
              "@type": "ItemList",
              itemListElement: structuredCases,
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: aiFaqItems.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.a,
              },
            })),
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首頁",
                item: "https://autochain.com.hk/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "ERP 企業管理系統",
                item: "https://autochain.com.hk/erp",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "案例分享",
                item: "https://autochain.com.hk/erp/case-studies",
              },
            ],
          }),
        }}
      />

      <Header />

      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-end">
            <div>
              <div className="inline-flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-700">
                <i className="ri-folders-line text-base"></i>
                ERP 案例中心
              </div>
              <h1 className="mt-6 max-w-4xl text-4xl font-bold leading-tight text-slate-950 md:text-6xl">
                看看不同行業公司，
                <span className="block text-teal-700">
                  如何整理 ERP 流程
                </span>
              </h1>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-600 md:text-xl">
                這裡整理不同公司在導入 ERP 前後常見的營運情境：哪些資料最容易分散、哪些部門最常重覆輸入、第一階段通常應該先整理哪一段流程。
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button
                  variant="primary"
                  size="lg"
                  className="px-7 py-4 text-base"
                  onClick={() =>
                    openWhatsApp(
                      "您好！我看過你們的 ERP 案例中心，想講講我們公司的流程，看看適合哪類 ERP 導入方式。",
                      "Hello! I reviewed your ERP case study hub and would like to discuss our workflow to see which ERP rollout approach fits.",
                    )
                  }
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  講講我們公司流程
                </Button>
                <Link
                  to="/erp"
                  className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-7 py-4 text-base font-semibold text-slate-700 transition hover:border-slate-500 hover:bg-slate-50"
                >
                  查看 ERP 方案
                </Link>
              </div>
            </div>

            <div className="space-y-4">
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50 shadow-sm">
                <img
                  src="/erp-og-image.png"
                  alt="ERP 案例流程示意"
                  className="h-48 w-full object-cover sm:h-56 lg:h-64"
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {[
                  ["10+", "行業案例入口"],
                  ["6", "常見 ERP 痛點"],
                  ["5", "行業 x 模組比較"],
                  ["FAQ", "導入常見問題"],
                ].map(([value, label]) => (
                  <div
                    key={label}
                    className="rounded-lg border border-slate-200 bg-slate-50 p-5"
                  >
                    <div className="text-3xl font-bold text-slate-950">
                      {value}
                    </div>
                    <div className="mt-2 text-sm font-medium text-slate-500">
                      {label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#f7f5ef] py-14">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-3xl">
              <span className="text-sm font-semibold uppercase tracking-[0.18em] text-teal-700">
                按行業查看
              </span>
              <h2 className="mt-3 text-3xl font-bold text-slate-950 md:text-5xl">
                行業案例資料庫
              </h2>
              <p className="mt-4 leading-8 text-slate-600">
                每個行業入口都用固定欄位整理：公司類型、常見問題、ERP 模組和導入後效果。
                未來新增文章時，可以直接接到相同行業或建立新行業分類。
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white px-5 py-4 text-sm leading-6 text-slate-600">
              新案例會持續加入：
              <span className="block font-semibold text-slate-900">
                更多行業、更多流程情境、更多導入例子
              </span>
            </div>
          </div>

          <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {erpCaseStudies.map((item, index) => {
              const cardClass =
                "block h-full rounded-lg border border-slate-200 bg-white p-6 shadow-sm transition";
              const interactiveClass = item.href
                ? "hover:-translate-y-1 hover:border-teal-200 hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-teal-100"
                : "";
              const cardContent = (
                <>
                  <div className="flex items-start justify-between gap-4">
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                      Case {String(index + 1).padStart(2, "0")}
                    </div>
                    <span
                      className={`rounded-lg px-3 py-1 text-xs font-semibold ${
                        item.stage === "已整理"
                          ? "bg-emerald-50 text-emerald-700"
                          : item.stage === "撰寫中"
                          ? "bg-amber-50 text-amber-700"
                          : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {item.stage}
                    </span>
                  </div>
                  <h3 className="mt-4 text-2xl font-bold leading-tight text-slate-950">
                    {item.industry}
                  </h3>
                  <p className="mt-2 text-sm font-semibold text-teal-700">
                    {item.companyType}
                  </p>
                  <p className="mt-4 text-lg font-semibold leading-7 text-slate-900">
                    {item.title}
                  </p>
                  <p className="mt-3 text-sm leading-7 text-slate-600">
                    {item.summary}
                  </p>

                  <div className="mt-5 border-t border-slate-100 pt-5">
                    <div className="text-sm font-semibold text-slate-900">
                      常見痛點
                    </div>
                    <ul className="mt-3 space-y-2">
                      {item.pains.slice(0, 2).map((pain) => (
                        <li
                          key={pain}
                          className="flex items-start gap-2 text-sm leading-6 text-slate-600"
                        >
                          <i className="ri-error-warning-line mt-1 text-orange-600"></i>
                          <span>{pain}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {item.modules.slice(0, 4).map((module) => (
                      <span
                        key={module}
                        className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600"
                      >
                        {module}
                      </span>
                    ))}
                  </div>
                  {item.href ? (
                    <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-teal-700">
                      閱讀完整案例
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  ) : (
                    <div className="mt-6 text-sm font-semibold text-slate-400">
                      完整案例準備中
                    </div>
                  )}
                </>
              );

              return item.href ? (
                <Link
                  key={item.slug}
                  id={item.slug}
                  to={item.href}
                  className={`${cardClass} ${interactiveClass}`}
                  aria-label={`閱讀${item.industry} ERP 完整案例`}
                >
                  {cardContent}
                </Link>
              ) : (
                <article
                  key={item.slug}
                  id={item.slug}
                  className={cardClass}
                >
                  {cardContent}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr]">
            <div>
              <span className="text-sm font-semibold uppercase tracking-[0.18em] text-orange-600">
                按問題查看
              </span>
              <h2 className="mt-3 text-3xl font-bold text-slate-950 md:text-5xl">
                按痛點找 ERP 案例
              </h2>
              <p className="mt-4 leading-8 text-slate-600">
                很多公司不是一開始就知道自己需要哪個 ERP 模組，而是先感覺到某段流程開始失控。
                這個區塊按日常痛點整理，方便你先找出最相似的營運情境。
              </p>
              <div className="mt-6 rounded-lg bg-slate-950 p-5 text-white">
                <div className="text-sm font-semibold text-orange-200">
                  建議閱讀方式
                </div>
                <p className="mt-2 text-sm leading-7 text-slate-300">
                  先看痛點是否相似，再看相關行業案例。行業不同也未必代表流程完全不同，很多中小企問題其實都圍繞訂單、庫存、採購和收付款。
                </p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {painPoints.map((item) => (
                <div
                  key={item.title}
                  className="rounded-lg border border-slate-200 bg-[#fbfaf7] p-5"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-teal-600 text-xl text-white">
                      <i className={item.icon}></i>
                    </div>
                    <h3 className="text-lg font-bold text-slate-950">
                      {item.title}
                    </h3>
                  </div>
                  <p className="mt-4 text-sm leading-7 text-slate-600">
                    {item.description}
                  </p>
                  <div className="mt-4 text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                    相關案例
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {item.relatedCases.map((caseName) => (
                      <span
                        key={caseName}
                        className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 ring-1 ring-slate-200"
                      >
                        {caseName}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-950 py-16 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <span className="text-sm font-semibold uppercase tracking-[0.18em] text-orange-200">
              快速比較
            </span>
            <h2 className="mt-3 text-3xl font-bold md:text-5xl">
              行業 x ERP 模組比較表
            </h2>
            <p className="mt-4 leading-8 text-slate-300">
              這張表幫你快速比較不同行業的 ERP 起步方式：通常是哪個部門最辛苦、哪段流程最需要先整理，以及第一階段應該由哪些模組承接。
            </p>
          </div>

          <div className="mt-10 overflow-x-auto rounded-lg border border-white/10">
            <table className="min-w-[980px] w-full border-collapse bg-slate-900 text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-white/5 text-slate-300">
                  <th className="px-5 py-4 font-semibold">行業</th>
                  <th className="px-5 py-4 font-semibold">常見問題</th>
                  <th className="px-5 py-4 font-semibold">涉及部門</th>
                  <th className="px-5 py-4 font-semibold">常用 ERP 模組</th>
                  <th className="px-5 py-4 font-semibold">建議第一步</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr key={row.industry} className="border-b border-white/10">
                    <td className="px-5 py-5 font-semibold text-white">
                      {row.industry}
                    </td>
                    <td className="px-5 py-5 leading-6 text-slate-300">
                      {row.commonProblem}
                    </td>
                    <td className="px-5 py-5 text-slate-300">
                      {row.departments}
                    </td>
                    <td className="px-5 py-5 text-orange-100">{row.modules}</td>
                    <td className="px-5 py-5 leading-6 text-teal-100">
                      {row.firstStep}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="bg-[#f7f5ef] py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <span className="text-sm font-semibold uppercase tracking-[0.18em] text-teal-700">
                常見問題
              </span>
              <h2 className="mt-3 text-3xl font-bold text-slate-950 md:text-5xl">
                ERP 導入常見問題
              </h2>
              <p className="mt-4 leading-8 text-slate-600">
                如果你正在比較 ERP，這幾個問題通常會最先出現：什麼時候需要導入、應該先做哪個範圍、匿名案例是否有參考價值，以及 AUTOCHAIN 會怎樣評估流程。
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {seoTopics.map((topic) => (
                  <span
                    key={topic}
                    className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-600"
                  >
                    {topic}
                  </span>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              {aiFaqItems.map((item) => (
                <article
                  key={item.q}
                  className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <h3 className="text-xl font-bold leading-7 text-slate-950">
                    {item.q}
                  </h3>
                  <p className="mt-3 leading-8 text-slate-600">{item.a}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <div className="text-sm font-semibold uppercase tracking-[0.2em] text-orange-600">
            AutoERP
          </div>
          <h2 className="mt-4 text-3xl font-bold leading-tight text-slate-950 md:text-5xl">
            如果你的流程和其中一個案例相似，
            <span className="block text-teal-700">可以先由最混亂的一段開始整理</span>
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-lg leading-8 text-slate-600">
            我們會先了解你公司現有 SOP，再建議第一階段應導入哪些 ERP 模組，
            讓系統先解決真實營運問題，再逐步擴充。
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Button
              variant="primary"
              size="lg"
              className="px-8 py-4 text-base"
              onClick={() =>
                openWhatsApp(
                  "您好！我看過你們的 ERP 案例分享，想講講我們公司現有流程，看看適合怎樣導入。",
                  "Hello! I have reviewed your ERP case studies and would like to share our current workflow to see how we should approach implementation.",
                )
              }
            >
              <i className="ri-whatsapp-line mr-2"></i>
              講講我們現有流程
            </Button>
            <Link
              to="/erp"
              className="inline-flex items-center justify-center rounded-lg bg-slate-950 px-8 py-4 text-base font-semibold text-white transition hover:bg-slate-800"
            >
              返回 ERP 主頁
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
